/**
 * SVG layout (D34): {@link MapGraph} → positioned drawing primitives.
 *
 * @remarks
 * The geometry lives HERE, once, so both SVG serializations agree by
 * construction: `renderMapSvg` turns these primitives into a string (for the
 * marketing site, a bridge, an email, a file), and the React map panel maps
 * the same primitives onto JSX elements (so the app needs no
 * `dangerouslySetInnerHTML`). Two serializations, one layout — the same
 * discipline the graph itself applies to derivation.
 *
 * Units are SVG user units; consumers scale via `width`/`height` or CSS.
 * Colors are NOT decided here: primitives carry semantic flags
 * (`knowledge`, `isActive`) and each serializer maps them onto CSS custom
 * properties or classes.
 *
 * **A label has a size, and the box holds it (D222).** The engine has no
 * font, so a label's width is estimated the way D174 estimates the
 * hologram's — from the stylesheet's own numbers, one row per selector that
 * sets its own tracking ({@link PLAN_CHAR_ADVANCE}). A name wider than the
 * cell's inner width is wrapped at its spaces onto at most two lines, which
 * the cell's height holds; a name that cannot be held in two lines at the
 * cell's width — one word too long to break, or too many words for two lines
 * — widens its OWN box, as little as will hold it in two. Nothing is clipped,
 * shrunk or cut (content standards §3.11, rules 3 and 4); a one-line label is
 * drawn exactly where it was before D222.
 *
 * **A corridor reaches the wall (D222).** An edge runs from the border of one
 * box to the border of the other, not from center to center. Until D222 the
 * ends were hidden rather than ended — a walked box's fill covered them, and
 * an unknown box, unfilled by design so its tier reads as unwalked, showed
 * the corridor running to the middle of its `???`.
 *
 * @packageDocumentation
 */

import type { MapGraph, MapNodeKnowledge } from './map-graph.js';

import { UNKNOWN_LABEL } from './derive-map-graph.js';
import { type CharAdvance, type LabelText, labelWidthOf } from './map-holo-labels.js';

/** Layout constants, in SVG user units. */
const CELL_WIDTH = 132;
const CELL_HEIGHT = 46;
const CELL_GAP_X = 44;
const CELL_GAP_Y = 38;
const CANVAS_PADDING = 18;
/** Clear air between a label and its box's border, each side. */
const LABEL_PADDING = 8;
/** The most lines a cell's height holds. */
const LABEL_LINE_LIMIT = 2;
/** Where a one-line label's baseline sits, below the box's center — unchanged since D34. */
const ONE_LINE_BASELINE_DROP = 5;
/** Where a two-line label's first baseline sits, above the box's center, and how far the second is below the first. */
const TWO_LINE_BASELINE_RISE = 2;
const LINE_HEIGHT = 13;

/**
 * The stylesheet's type for the plan, as numbers: `.orpheus-map__label` is
 * 11px monospace — 6.6 a character — with 0.06em tracking, so 7.26 a
 * character; an unknown room's label (`.orpheus-map__node--unknown
 * .orpheus-map__label`) carries 0.2em instead, so 8.8. One row per selector
 * that sets its own tracking, as D174's table for the hologram. Without the
 * stylesheet the type is narrower, so the estimate is the wider case and the
 * bare render has slack.
 */
export const PLAN_CHAR_ADVANCE: CharAdvance = { name: 7.26, unknown: 8.8 };

/** One positioned link between two boxes' borders. */
export interface MapSvgEdgePrimitive {
	readonly x1: number;
	readonly x2: number;
	readonly y1: number;
	readonly y2: number;
}

/** One line of a node's label: its text and its baseline. */
export interface MapSvgLabelLine {
	readonly text: string;
	readonly y: number;
}

/** A label's lines as drawn — never none, so a serializer needs no fallback for a label with no first line. */
export type MapSvgLabelLines = readonly [MapSvgLabelLine, ...MapSvgLabelLine[]];

/** One positioned node box with its label. */
export interface MapSvgNodePrimitive {
	readonly height: number;
	readonly isActive: boolean;
	readonly knowledge: MapNodeKnowledge;
	/** The text to draw (the room's name, or the unknown placeholder), whole. */
	readonly label: string;
	/** The label's horizontal center — every line is anchored to it. */
	readonly labelX: number;
	/** The label as drawn: one line, or two when the name is wider than the cell's inner width. */
	readonly lines: MapSvgLabelLines;
	/** The cell's width, or wider when the label cannot be held in two lines at it. */
	readonly width: number;
	readonly x: number;
	readonly y: number;
}

/** A complete positioned drawing, ready for any SVG serializer. */
export interface MapSvgLayout {
	/** Long-form description for `<desc>` / `aria-describedby`. */
	readonly description: string;
	readonly edges: readonly MapSvgEdgePrimitive[];
	readonly height: number;
	readonly nodes: readonly MapSvgNodePrimitive[];
	readonly width: number;
}

/**
 * Position one derived map for drawing.
 *
 * @param graph - The derived map
 * @returns The positioned primitives
 */
export const buildMapSvgLayout = (graph: MapGraph): MapSvgLayout => {
	const { maxX, maxY, minX, minY } = graph.bounds;
	const columnCount = maxX - minX + 1;
	const rowCount = maxY - minY + 1;
	// Each node placed with the cell it stands on, so the edges can read the
	// box widths the labels decided — a widened box moves where its corridors end.
	const placed = graph.nodes.map((node) => {
		const [centerX, centerY] = cellCenter(node.x, node.y, graph.bounds);
		const name = node.name ?? UNKNOWN_LABEL;
		const text: LabelText = { label: node.isActive ? `▸ ${name}` : name, type: node.knowledge === 'unknown' ? 'unknown' : 'name' };
		const { lines, width } = fitLabel(text);
		const [firstLine, ...moreLines] = lines;
		const baselineOf = (index: number): number =>
			moreLines.length === 0 ? centerY + ONE_LINE_BASELINE_DROP : centerY - TWO_LINE_BASELINE_RISE + index * LINE_HEIGHT;
		const primitive: MapSvgNodePrimitive = {
			height: CELL_HEIGHT,
			isActive: node.isActive,
			knowledge: node.knowledge,
			label: text.label,
			labelX: centerX,
			lines: [{ text: firstLine, y: baselineOf(0) }, ...moreLines.map((lineText, index) => ({ text: lineText, y: baselineOf(index + 1) }))],
			width,
			x: centerX - width / 2,
			y: centerY - CELL_HEIGHT / 2,
		};

		return { cell: cellKey(node.x, node.y), primitive };
	});
	const halfWidthsByCell = new Map(placed.map(({ cell, primitive }) => [cell, primitive.width / 2]));
	const halfWidthAt = (x: number, y: number): number => halfWidthsByCell.get(cellKey(x, y)) ?? CELL_WIDTH / 2;

	return {
		description: describeGraph(graph),
		edges: graph.edges.map((edge) => {
			const from = cellCenter(edge.fromX, edge.fromY, graph.bounds);
			const to = cellCenter(edge.toX, edge.toY, graph.bounds);
			const [x1, y1] = borderPoint(from, to, halfWidthAt(edge.fromX, edge.fromY));
			const [x2, y2] = borderPoint(to, from, halfWidthAt(edge.toX, edge.toY));

			return { x1, x2, y1, y2 };
		}),
		height: rowCount * CELL_HEIGHT + (rowCount - 1) * CELL_GAP_Y + CANVAS_PADDING * 2,
		nodes: placed.map(({ primitive }) => primitive),
		width: columnCount * CELL_WIDTH + (columnCount - 1) * CELL_GAP_X + CANVAS_PADDING * 2,
	};
};

/**
 * A label's lines, typed so the LAST always exists — which is how the wrap
 * builds them: the lines it has closed, then the one still open.
 */
type LabelLines = readonly [...string[], string];

/**
 * Break a label into the lines its box draws, and size the box to hold them.
 *
 * @remarks
 * A greedy wrap at the label's spaces, against the cell's inner width: words
 * join a line while the line still fits, and start the next one when it
 * would not. Two lines or fewer at the cell's width is the common case and
 * the box stays the cell. Past that, the box widens rather than the label
 * losing anything: a lone word wider than the cell is drawn whole on a box
 * that holds it, and a name of more than two lines' worth of words is split
 * once, at whichever space leaves the longer of its two lines shortest, and
 * the box widens to that line. The width is the estimate's, with the
 * padding each side.
 *
 * @param text - The label and the type it is set in
 * @returns The lines, and the box width that holds them
 */
const fitLabel = (text: LabelText): { readonly lines: LabelLines; readonly width: number } => {
	const innerWidth = CELL_WIDTH - LABEL_PADDING * 2;
	const widthOf = (line: string): number => labelWidthOf({ label: line, type: text.type }, PLAN_CHAR_ADVANCE);
	const [firstWord, ...otherWords] = splitIntoWords(text.label);
	const wrap = otherWords.reduce<{ readonly closed: readonly string[]; readonly open: string }>(
		({ closed, open }, word) =>
			widthOf(`${open} ${word}`) <= innerWidth ? { closed, open: `${open} ${word}` } : { closed: [...closed, open], open: word },
		{ closed: [], open: firstWord },
	);
	const greedy: LabelLines = [...wrap.closed, wrap.open];
	const lines: LabelLines = greedy.length <= LABEL_LINE_LIMIT ? greedy : splitInTwo([firstWord, ...otherWords], widthOf);
	const widest = Math.max(...lines.map(widthOf));

	return { lines, width: widest <= innerWidth ? CELL_WIDTH : widest + LABEL_PADDING * 2 };
};

/**
 * A label's words. `split` never returns an empty array (`''.split(' ')` is
 * `['']`), so the first word needs no runtime fallback — the same fact the
 * parser's `splitIntoNonEmptySequence` states, local here per the R1 ruling.
 *
 * @param label - The label
 * @returns Its words, typed non-empty
 */
const splitIntoWords = (label: string): readonly [string, ...string[]] => label.split(' ') as [string, ...string[]];

/**
 * Split words into exactly two lines at the space that leaves the longer line
 * shortest — the narrowest box that holds the whole label in two lines.
 *
 * @param words - The label's words, three or more (fewer wrap greedily into two lines or one)
 * @param widthOf - The width of one line as drawn
 * @returns The two lines
 */
const splitInTwo = (words: readonly [string, ...string[]], widthOf: (line: string) => number): readonly [string, string] => {
	const [firstWord, ...otherWords] = words;
	const widthOfPair = (pair: readonly [string, string]): number => Math.max(widthOf(pair[0]), widthOf(pair[1]));

	return otherWords.reduce<readonly [string, string]>(
		(narrowest, _word, index) => {
			const candidate: readonly [string, string] = [[firstWord, ...otherWords.slice(0, index)].join(' '), otherWords.slice(index).join(' ')];

			return widthOfPair(candidate) < widthOfPair(narrowest) ? candidate : narrowest;
		},
		[firstWord, otherWords.join(' ')],
	);
};

/**
 * Where the line from one box's center toward another's leaves the first
 * box's border: the cell's half-height across, the box's own half-width
 * along, whichever the line reaches first. A line toward a cell no node
 * holds (a graph the engine did not derive) stops where the cell's border
 * would be.
 *
 * @param from - The center the line leaves
 * @param toward - The center it runs toward
 * @param halfWidth - Half the width of the box it leaves
 * @returns The point on the border, to a tenth of a unit
 */
const borderPoint = (from: readonly [number, number], toward: readonly [number, number], halfWidth: number): readonly [number, number] => {
	const [fromX, fromY] = from;
	const deltaX = toward[0] - fromX;
	const deltaY = toward[1] - fromY;
	const alongX = deltaX === 0 ? Number.POSITIVE_INFINITY : halfWidth / Math.abs(deltaX);
	const alongY = deltaY === 0 ? Number.POSITIVE_INFINITY : CELL_HEIGHT / 2 / Math.abs(deltaY);
	const reach = Math.min(alongX, alongY);

	return [tenth(fromX + reach * deltaX), tenth(fromY + reach * deltaY)];
};

/** A number to a tenth of a unit — enough for a drawing, and stable across platforms. */
const tenth = (value: number): number => Math.round(value * 10) / 10;

/** The key one grid cell is looked up by. */
const cellKey = (x: number, y: number): string => `${String(x)},${String(y)}`;

/**
 * The accessible description: the same facts the ASCII header and legend
 * state, as a sentence a screen reader can read straight through. Shared
 * with the hologram layout (D169), which adds its floors to it.
 *
 * @param graph - The derived map
 * @returns The description text
 */
export const describeGraph = (graph: MapGraph): string => {
	// One total pass, deliberately: `filter` does not narrow the ELEMENT type, so
	// a filtered-then-mapped `node.name` still read `string | undefined` and
	// demanded a `?? ''` fallback that nothing could reach — a phantom branch
	// §14.1 says to restructure away. `flatMap` selects and narrows in the same
	// step, so the fallback stops existing.
	const visitedNames = graph.nodes
		.flatMap((node) => (node.knowledge === 'visited' && node.name !== undefined ? [node.name] : []))
		.sort((left, right) => left.localeCompare(right, 'en'));
	const activeName = graph.nodes.find((node) => node.isActive)?.name ?? UNKNOWN_LABEL;
	const unknownCount = graph.nodes.filter((node) => node.knowledge === 'unknown').length;
	// D191: labeled readouts rather than counted nouns. This description is
	// composed by the ENGINE out of the pack's room names, so English agreement
	// compiled in here is D183's defect one layer down — and the old clause did
	// not even manage English: it agreed the noun and left the verb, so at one
	// it read "1 unexplored way lead onward". Half-done agreement is the
	// argument against doing it at all.
	//
	// The count stays beside the list rather than being inferred from it:
	// `visitedCount` is every visited room, and `visitedNames` is the visited
	// rooms that HAVE a name, so the two can legitimately differ.
	const unknownClause = unknownCount === 0 ? '' : ` Unexplored ways onward: ${String(unknownCount)}.`;

	return `Locations walked: ${String(graph.visitedCount)} — ${visitedNames.join(', ')}. Currently in ${activeName}.${unknownClause}`;
};

/** The pixel center of one grid cell. */
const cellCenter = (x: number, y: number, bounds: MapGraph['bounds']): readonly [number, number] => [
	CANVAS_PADDING + (x - bounds.minX) * (CELL_WIDTH + CELL_GAP_X) + CELL_WIDTH / 2,
	CANVAS_PADDING + (y - bounds.minY) * (CELL_HEIGHT + CELL_GAP_Y) + CELL_HEIGHT / 2,
];
